import { Container } from "../../../shared/components/Container";
import { useHomeData } from "../../../shared/hooks/useHome";
import { HeroCarousel, ProductCarousel } from "@shared/components/carousels";
import { HeroSkeleton, CatalogSkeleton } from "@shared/components/skeletons";
import { SeoHead } from "@shared/components";
import { generateOrganizationSchema } from "@shared/utils/schemaOrg";
import { Link } from "react-router-dom";
import { ShoppingBag, CreditCard, Check } from "lucide-react";
import type { NavbarCatalog } from "@shared/types/homepage";
import type {
  CarouselProduct,
  StandardCarouselWithProducts,
} from "@shared/types/carousel";

export function Home() {
  const { data, isLoading, error } = useHomeData();

  // 🏠 LOG: Data recibida en el componente (ya procesada por useHomeData)
  console.log("🏠 [HOME COMPONENT] Data recibida:", data);

  const defaultImage =
    "https://res.cloudinary.com/dq8ig7aly/image/upload/v1749660976/no-image_lbt2of.jpg";

  return (
    <>
      <SeoHead
        title="Inicio"
        description="Cerámicas Gassull: Especialistas en cerámicas y materiales de construcción de alta calidad. Más de 25 años de experiencia en Mendoza, Argentina. Explora nuestro catálogo completo."
        keywords="cerámicas, materiales construcción, Mendoza, Argentina, ladrillos, cemento, viguetas, revestimientos, construcción, cerámicas Gassull"
        canonical="/"
        schemaJsonLd={generateOrganizationSchema()}
      />
      <div className="min-h-screen">
      {/* Hero Carousel Section */}
      <section className="mb-16 ">
        {isLoading ? (
          <HeroSkeleton />
        ) : error ? (
          <div className="bg-red-50 border border-red-200 p-8 text-center">
            <p className="text-red-600">
              Error al cargar el hero carousel: {error.message}
            </p>
          </div>
        ) : data?.heroCarousel ? (
          <HeroCarousel data={data.heroCarousel} />
        ) : (
          <div className="bg-gray-50 p-8 text-center">
            <p className="text-gray-600">No hay hero carousel disponible</p>
          </div>
        )}
      </section>

      {/* Categories Section */}
      <div className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nuestros Catálogos
            </h2>
            <p className="text-lg text-gray-600">
              Explora nuestras categorías especializadas
            </p>
          </div>

          {isLoading ? (
            <CatalogSkeleton count={3} />
          ) : data?.navbar?.catalogs && data.navbar.catalogs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.navbar.catalogs.map((catalog: NavbarCatalog) => {
                // 📦 LOG: Catálogo completo (data cruda) - usando JSON para ver todas las propiedades
                console.log("📦 [CATALOG CARD - HOME] Catálogo completo (JSON):", JSON.parse(JSON.stringify(catalog)));

                // Usar la imagen del catálogo si está disponible, sino usar la imagen por defecto
                const backgroundImage = catalog.image || defaultImage;

                return (
                  <Link
                    key={catalog.id}
                    to={`/catalog/${catalog.slug}`}
                    state={{ categories: catalog.categories }}
                    className="block"
                  >
                    <Container
                      variant="category-card"
                      background={backgroundImage}
                      className="h-48 flex flex-col justify-between group hover:scale-105 transition-transform duration-200"
                      padding="lg"
                    >
                      {/* Nombre del catálogo centrado */}
                      <div className="flex-1 flex items-center justify-center">
                        <h3 className="text-white font-bold text-xl text-center leading-tight">
                          {catalog.name}
                        </h3>
                      </div>

                      {/* CTA en la esquina inferior derecha */}
                      <div className="flex justify-end">
                        <span className="text-white text-sm font-medium">
                          Ver productos →
                        </span>
                      </div>
                    </Container>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center">
              <Container
                variant="simple-box"
                padding="lg"
                className="max-w-md mx-auto"
              >
                <p className="text-gray-600">
                  No hay catálogos disponibles en este momento.
                </p>
              </Container>
            </div>
          )}
        </div>
      </div>

      {/* Standard Carousels Section */}
      {data?.standardCarousels && data.standardCarousels.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
            {data.standardCarousels.map(
              (carousel: StandardCarouselWithProducts) => {
                const products: CarouselProduct[] = carousel.products.map(
                  (product) => {
                    const imageUrl = product.primaryImage ?? defaultImage;
                    return {
                      _id: product.id,
                      name: product.name,
                      slug: product.slug,
                      images: [
                        {
                          url: imageUrl,
                          publicId: imageUrl,
                          _id: `${product.id}-image`,
                        },
                      ],
                    };
                  }
                );

                return (
                  <ProductCarousel
                    key={carousel.id}
                    title={carousel.name}
                    subtitle={carousel.description ?? undefined}
                    products={products}
                    className="relative"
                  />
                );
              }
            )}
          </div>
        </section>
      )}

      {/* Features Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              ¿Por qué elegirnos?
            </h2>
            <p className="text-lg text-gray-600">
              Calidad y experiencia al servicio de tu proyecto
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Container variant="default" padding="lg" className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-blue-600 text-2xl">🏗️</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Calidad Garantizada
              </h3>
              <p className="text-gray-600">
                Productos de la más alta calidad con garantía de durabilidad.
              </p>
            </Container>

            <Container variant="default" padding="lg" className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-green-600 text-2xl">🚚</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Entrega Rápida
              </h3>
              <p className="text-gray-600">
                Entregamos en toda la región con la mayor eficiencia.
              </p>
            </Container>

            <Container variant="default" padding="lg" className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-purple-600 text-2xl">👨‍💼</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Asesoramiento Experto
              </h3>
              <p className="text-gray-600">
                Nuestro equipo te ayuda a elegir los mejores productos.
              </p>
            </Container>
          </div>
        </div>
      </div>

      {/* Servicios y Métodos de Pago */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Servicios y Métodos de Pago
            </h2>
            <p className="text-lg text-gray-600">
              Opciones flexibles para tu comodidad
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Opciones de Servicio */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="bg-[#282828] rounded-lg p-2 mr-3">
                  <ShoppingBag className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  Opciones de servicio
                </h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-gray-700">
                  <Check className="h-4 w-4 text-[#282828] mr-2 flex-shrink-0" />
                  Entrega a domicilio
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Check className="h-4 w-4 text-[#282828] mr-2 flex-shrink-0" />
                  Compras en tienda
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Check className="h-4 w-4 text-[#282828] mr-2 flex-shrink-0" />
                  Retiro en tienda
                </li>
              </ul>
            </div>

            {/* Métodos de Pago */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="bg-[#282828] rounded-lg p-2 mr-3">
                  <CreditCard className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  Pagos
                </h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-gray-700">
                  <Check className="h-4 w-4 text-[#282828] mr-2 flex-shrink-0" />
                  Pagos móviles mediante NFC
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Check className="h-4 w-4 text-[#282828] mr-2 flex-shrink-0" />
                  Tarjetas de débito
                </li>
                <li className="flex items-center text-sm text-gray-700">
                  <Check className="h-4 w-4 text-[#282828] mr-2 flex-shrink-0" />
                  Tarjetas de crédito
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
